import {Button, IconButton} from "@mui/material";
import {FiPlus, FiTrash2} from "react-icons/fi";
import {useLocation, useNavigate} from "react-router-dom";
import {useCallback, useMemo} from "react";
import ListView from "../components/ListView/ListView";
import Window from "../components/Window/Window";
import SearchBar from "../components/ui/SearchBar";
import ExportCsvButton from "../components/ui/ExportCsvButton";
import {tenantsColumns, tenantsExportColumns} from "../constants/dataGridColumns";
import {tenantSearchFields} from "../constants/searchFields";
import useApiRequest from "../hooks/useApiRequest";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import useConfirmDialog from "../hooks/useConfirmDialog";
import usePaginationSearch from "../hooks/usePaginationSearch";
import LoadingContent from "../components/ui/LoadingContent";
import {fetchAllPages} from "../utils/pagination";
import {useAuthPermissions, ACTIONS, RESOURCES} from "../hooks/useAuthPermissions";
import {snackbar} from "../utils/snackbar";

const ROLE_OPTIONS_LIMIT = 100;

const Tenants = () => {
  const navigate = useNavigate();
  const {state} = useLocation();
  const {data, loading, list, remove} = useApiRequest({baseUrl: "/tenants", resource: "tenants"});

  const {list: listForExport} = useApiRequest({baseUrl: "/tenants", resource: "tenants"});
  const {can} = useAuthPermissions();

  const canReadRoles = can(ACTIONS.READ, RESOURCES.ROLES);
  const {list: listRoles} = useApiRequest({baseUrl: "/roles", resource: "roles"});

  // Opciones del filtro de rol: los nombres vienen del backend, no de una lista fija.
  const loadRoles = useCallback(
    async (term) => {
      const res = await listRoles({...(term ? {name: term} : {}), limit: ROLE_OPTIONS_LIMIT}, {silentError: true});
      const roles = Array.isArray(res?.data?.data) ? res.data.data : [];
      const names = [...new Set(roles.map((role) => role.name).filter(Boolean))];
      names.sort((a, b) => a.localeCompare(b));
      return names.map((name) => ({id: name, label: name}));
    },
    [listRoles]
  );

  const fields = useMemo(
    () =>
      tenantSearchFields
        .filter((field) => field.field !== "only_linked_role" || canReadRoles)
        .map((field) => (field.field === "only_linked_role" ? {...field, loadOptions: loadRoles} : field)),
    [canReadRoles, loadRoles]
  );

  const fetcher = useCallback(
    async (query) => {
      const {success, data} = await list({...query});
      if (!success) return null;

      return {data};
    },
    [list]
  );

  const {filters, applyFilters, resetFilters, dataGridProps, refresh, listState} = usePaginationSearch({
    fetcher,
    initialListState: state?.listState
  });

  const handleTenantClick = (tenantId) => {
    if (tenantId) {
      navigate(`/tenant-form`, {state: {id: tenantId, from: {pathname: "/tenants", listState}}});
    }
  };

  const {confirmOpen, itemName, openDialog, handleConfirm, handleCancel} = useConfirmDialog(async (id) => {
    const {success} = await remove(id);
    if (!success) return;

    snackbar.success("Tenant desactivado correctamente.");
    refresh();
  });

  const handleOpenDeleteDialog = (id) => {
    const found = Array.isArray(data?.data) ? data.data.find((tenant) => tenant.id === id) : null;
    openDialog(id, found?.name || "");
  };

  const columns = [
    ...tenantsColumns.filter((col) => col.field !== "email"),
    ...(can(ACTIONS.DELETE, RESOURCES.TENANTS)
      ? [
          {
            field: "actions",
            headerName: "Acciones",
            width: 150,
            sortable: false,
            filterable: false,
            renderCell: (params) =>
              params.row.status !== "deleted" && (
                <IconButton
                  aria-label="delete"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenDeleteDialog(params.row.id);
                  }}
                >
                  <FiTrash2 />
                </IconButton>
              )
          }
        ]
      : [])
  ];

  const getRowClassName = (params) => {
    return params.row.status === "deleted" ? "row-disabled" : "";
  };

  return (
    <Window
      title={"Tenants"}
      topRightButtons={
        <>
          <ExportCsvButton
            columns={tenantsExportColumns}
            filename="tenants"
            fetchRows={() => fetchAllPages(listForExport, filters)}
          />

          {can(ACTIONS.WRITE, RESOURCES.TENANTS) && (
            <Button variant="contained" startIcon={<FiPlus />} onClick={() => navigate("/tenant-form")}>
              Nuevo Tenant
            </Button>
          )}
        </>
      }
    >
      {loading ? (
        <LoadingContent />
      ) : (
        <>
          <SearchBar fields={fields} initialValues={filters} onSearch={applyFilters} onReset={resetFilters} />
          <ListView
            columns={columns}
            getRowId={(row) => row.id}
            loading={loading}
            dataGridProps={dataGridProps}
            onRowClick={(row) => handleTenantClick(row.id)}
            getRowClassName={getRowClassName}
          />
        </>
      )}
      <ConfirmDialog
        open={confirmOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        entityName="tenant"
        entityLabel={itemName}
        title="Desactivar "
        confirmText="Desactivar"
        description={
          <>
            ¿Seguro que desea desactivar el tenant
            {itemName && (
              <>
                {" "}
                <strong>{itemName}</strong>
              </>
            )}
            ? <br />
            El tenant no se eliminará, pero quedará <strong>desactivado</strong> y no estará disponible.
          </>
        }
      />
    </Window>
  );
};

export default Tenants;
